<template>
  <div
    class="filter-input-view lisaui:flex lisaui:border lisaui:rounded-md lisaui:bg-background lisaui:shadow-sm lisaui:border-border lisaui:border"
  >
    <TransitionGroup
      name="filter-flight"
      tag="div"
      class="filter-box lisaui:flex lisaui:items-center"
    >
      <LISAButton
        v-if="!filtersAreEmpty"
        key="refresh-filter"
        class="filter-utility-button"
        size="sm"
        variant="neutral"
        text
        icon="fas fa-sync"
        :aria-label="t('components.eventAction.filter.FilterView.Reload')"
        @click="refreshFilter"
      />

      <component
        :is="badgeForFilter(filter)"
        v-for="{ filter, index } in quickSearchFilters"
        :key="filterKey(filter)"
        class="quickSearchBadge"
        :types="eventTypes"
        :filter="filter"
        :mode="filter instanceof IDFilter ? FilterEntity.EVENT : undefined"
        @click="selectFilter(index, FilterEntity.EVENT)"
        @remove="removeFilter(index, FilterEntity.EVENT)"
      />

      <span v-if="filtersAreEmpty" key="empty-filter" class="empty-filter-message">
        {{ t("components.eventActionBoard.filter.FilterView.noFilterAvailable") }}
      </span>

      <component
        :is="badgeForFilter(filter)"
        v-for="{ filter, index } in activeEventFilters"
        :key="filterKey(filter)"
        class="eventBadge"
        :types="eventTypes"
        :filter="filter"
        :mode="filter instanceof IDFilter ? FilterEntity.EVENT : undefined"
        @click="selectFilter(index, FilterEntity.EVENT)"
        @remove="removeFilter(index, FilterEntity.EVENT)"
      />

      <component
        :is="badgeForFilter(filter)"
        v-for="(filter, index) in actionFilters"
        :key="filterKey(filter)"
        class="actionBadge"
        :types="actionTypes"
        :filter="filter"
        :mode="filter instanceof IDFilter ? FilterEntity.ACTION : undefined"
        @click="selectFilter(index, FilterEntity.ACTION)"
        @remove="removeFilter(index, FilterEntity.ACTION)"
      />
    </TransitionGroup>

    <div class="filter-actions text-nowrap lisaui:flex lisaui:items-center">
      <SavedFilterManager compact :board-id="boardId" :filters="serializedFilters" />
      <LISAButton
        class="filter-utility-button"
        size="sm"
        variant="neutral"
        text
        icon="fas fa-times"
        :aria-label="t('global.reset')"
        @click="removeAllFilters"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, type PropType } from "vue";
import {
  type ActionFilter,
  type EventFilter,
  Filter,
  FilterEntity,
  type FilterEventActionTypes,
} from "./FilterTypes";
import FilterSerializer from "./FilterSerializer";
import SavedFilterManager from "./SavedFilterManager.vue";
import QuickSearchFilter from "./filterImplementations/QuickSearchFilter";
import IDFilter from "./filterImplementations/IDFilter";
import { LISAButton } from "@fi-solutions/lisa-ui";

const props = defineProps({
  eventFilters: {
    required: true,
    type: Array as PropType<Array<EventFilter>>,
  },
  actionFilters: {
    required: true,
    type: Array as PropType<Array<ActionFilter>>,
  },
  eventTypes: {
    type: Array as PropType<FilterEventActionTypes[]>,
    required: false,
    default: () => [],
  },
  actionTypes: {
    type: Array as PropType<FilterEventActionTypes[]>,
    required: false,
    default: () => [],
  },
  boardId: {
    type: Number,
    required: false,
    default: undefined,
  },
});
const emit = defineEmits(["filterSelected", "filterRemoved", "allFiltersRemoved", "refreshFilter"]);
const filterKeys = new WeakMap<Filter, number>();
let nextFilterKey = 0;
function filterKey(filter: Filter) {
  const existing = filterKeys.get(filter);
  if (existing != null) {
    return existing;
  }
  const key = nextFilterKey++;
  filterKeys.set(filter, key);
  return key;
}

const selectFilter = (index: number, type: FilterEntity) => {
  emit("filterSelected", { type, index });
};
const removeFilter = (index: number, type: FilterEntity) => {
  emit("filterRemoved", { type, index });
};
const removeAllFilters = () => {
  emit("allFiltersRemoved");
};
const refreshFilter = () => {
  emit("refreshFilter");
};
const badgeForFilter = (filter: Filter) => {
  const filterClass = filter.constructor as typeof Filter;
  return filterClass.Badge;
};
const filtersAreEmpty = computed(
  () => props.eventFilters.length === 0 && props.actionFilters.length === 0,
);
const quickSearchFilters = computed(() =>
  props.eventFilters.flatMap((filter, index) =>
    filter instanceof QuickSearchFilter ? [{ filter, index }] : [],
  ),
);
const activeEventFilters = computed(() =>
  props.eventFilters.flatMap((filter, index) =>
    filter instanceof QuickSearchFilter ? [] : [{ filter, index }],
  ),
);
const serializedFilters = computed(() =>
  FilterSerializer.serialize({
    eventFilters: props.eventFilters,
    actionFilters: props.actionFilters,
  }),
);

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.filter-input-view {
  min-width: 0;
  align-items: stretch;
  gap: 0.35rem;
  padding: 0.3rem 0.4rem;
}

.eventBadge {
  background-color: #b1d2b1 !important;
  padding-top: 0;
  padding-bottom: 0;
}

.actionBadge {
  background-color: #fedcba !important;
  padding-top: 0;
  padding-bottom: 0;
}

.quickSearchBadge {
  background-color: var(--p-primary-color) !important;
  color: var(--p-primary-contrast-color) !important;
  padding-top: 0;
  padding-bottom: 0;
}

.filter-box {
  min-width: 0;
  flex: 1 1 auto;
  gap: 0.3rem;
  overflow-x: auto;
  overflow-y: hidden;
  line-height: 1.8em;
  white-space: nowrap;
  scrollbar-width: thin;
}

.empty-filter-message {
  align-self: center;
  color: var(--p-text-muted-color);
}

.filter-actions {
  flex: 0 0 auto;
  gap: 0.15rem;
}

.filter-utility-button {
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  align-self: center;

  :deep(.lisa-button) {
    width: 100%;
    height: 100%;
    padding: 0;
  }
}

.filter-flight-enter-active {
  transition:
    opacity 240ms ease-out,
    transform 320ms cubic-bezier(0.2, 0.9, 0.3, 1.2);
}

.filter-flight-enter-from {
  opacity: 0;
  transform: translateY(-3.5rem) scale(0.8);
}

@media (prefers-reduced-motion: reduce) {
  .filter-flight-enter-active {
    transition-duration: 1ms;
  }
}
</style>
